import React, { useState, useEffect } from "react";
import { InteractionModalContainer } from ".";
import { Row, Col } from "react-bootstrap";
import RiskMetrics from "../RiskMetrics";
import { formatFloat, formatPercentage } from "../../utils/format";
import InputAmount from "../InputAmount";
import InteractionButton from "../InteractionButton";
import BorrowFee from "./TransactionRecap/BorrowFee";
import useApprove from "../../hooks/useApprove";
import useLeverage from "../../hooks/useLeverage";
import useGenericCall from "../../hooks/useGenericCall";
import { useOnchainAccountLendingPool } from "../../hooks/useOnchain";
import { getWeightedAPR } from "../../utils";
import { useToBigNumber } from "../../hooks/useConverter";
import { PoolTokenType, ApprovalType } from "impermax-sdk";


export interface LeverageInteractionModalProps {
  show: boolean;
  toggleShow(s: boolean): void;
}

export default function LeverageInteractionModal({show, toggleShow}: LeverageInteractionModalProps) {
  const [newLeverage, setNewLeverage] = useState<number>(0);
  const slippage = 1.5; //50%

  const data = useGenericCall(useOnchainAccountLendingPool(), async onchainAccountLendingPool => {
    const offchainLendingPool = await onchainAccountLendingPool.getLendingPool().getOffchainLendingPool();
    const changeAmounts = await onchainAccountLendingPool.getInteractionsLendingPool().getLeverageAmounts(newLeverage, slippage);
    const maxLeverage = await onchainAccountLendingPool.getMaxLeverage();
    const currentLeverage = await onchainAccountLendingPool.getLeverage();
    return {
      symbol: await offchainLendingPool.getCollateral().getSymbol(),
      changeAmounts,
      maxLeverage,
      currentLeverage,
      totalAPR: await offchainLendingPool.getTotalAPR() * newLeverage,
      stakingRewards: await offchainLendingPool.getStakingRewards(),
      invalidInput: newLeverage < currentLeverage || newLeverage > maxLeverage,
      b: await Promise.all([onchainAccountLendingPool.getBorrowableA(), onchainAccountLendingPool.getBorrowableB()].map(
        async onchainAccountBorrowable => {
          const offchainBorrowable = await onchainAccountBorrowable.getPoolToken().getOffchainBorrowable();
          const amount = offchainBorrowable.getPoolTokenType() === PoolTokenType.BorrowableA ? changeAmounts.bAmountA : changeAmounts.bAmountB;
          return {
            symbol: await offchainBorrowable.getSymbol(),
            borrowAPR: await offchainBorrowable.getNextBorrowAPR(amount),
            farmingAPR: await offchainBorrowable.getNextFarmingAPR(amount),
            farmingRewards: await offchainBorrowable.getNextFarmingRewards(amount),
            borrowedUSD: (amount + await onchainAccountBorrowable.getBorrowed()) * await offchainBorrowable.getTokenPriceAccurate(),
          }
        }
      ))
    }
  }, [newLeverage, slippage]);

  // Get leverage info
  useEffect(() => {
    if (data && newLeverage === 0) setNewLeverage(Math.ceil(data.currentLeverage * 1000) / 1000);
  }, [data]);

  // Convert to big numbers
  const amountA = useToBigNumber(data ? data.changeAmounts.bAmountA : null, PoolTokenType.BorrowableA);
  const amountB = useToBigNumber(data ? data.changeAmounts.bAmountB : null, PoolTokenType.BorrowableB);
  const amountAMin = useToBigNumber(data ? data.changeAmounts.bAmountAMin : null, PoolTokenType.BorrowableA);
  const amountBMin = useToBigNumber(data ? data.changeAmounts.bAmountBMin : null, PoolTokenType.BorrowableB);

  // Interactions
  const invalidInput = data && data.invalidInput;
  const [approvalStateA, onApproveA, permitDataA] = useApprove(ApprovalType.BORROW, amountA, invalidInput, PoolTokenType.BorrowableA);
  const [approvalStateB, onApproveB, permitDataB] = useApprove(ApprovalType.BORROW, amountB, invalidInput, PoolTokenType.BorrowableB);
  const [leverageState, leverage] = useLeverage(approvalStateA, approvalStateB, invalidInput, amountA, amountB, amountAMin, amountBMin, permitDataA, permitDataB);
  const onLeverage = async () => {
    await leverage();
    toggleShow(false);
  }

  const [farmingRewards, setFarmingRewards] = useState<Array<{APR: number, symbol: string}>>([]);
  useEffect(() => {
    if (!data) return;
    const nextWeightA = newLeverage <= 1 ? 1 : data.b[0].borrowedUSD / (data.b[0].borrowedUSD + data.b[1].borrowedUSD);
    let borrowMultiplierA = nextWeightA * (newLeverage - 1);
    let borrowMultiplierB = (1 - nextWeightA) * (newLeverage - 1);
    // Assume both pools are receiving the same tokens as reward
    const farmingRewards = [];
    for (let i = 0; i < data.b[0].farmingRewards.length; i++) {
      if (!data.b[1].farmingRewards[i] || data.b[0].farmingRewards[i].symbol != data.b[1].farmingRewards[i].symbol) {
        console.error("Different rewards");
        continue;
      }
      farmingRewards.push({
        APR: data.b[0].farmingRewards[i].APR * borrowMultiplierA + data.b[1].farmingRewards[i].APR * borrowMultiplierB,
        symbol: data.b[0].farmingRewards[i].symbol,
      });
    }
    setFarmingRewards(farmingRewards);
  }, [data]);

  if (!data) return null;

  const borrowAPR = getWeightedAPR(newLeverage, data.b[0].borrowedUSD, data.b[0].borrowAPR, data.b[1].borrowedUSD, data.b[1].borrowAPR);
  const farmingAPR = getWeightedAPR(newLeverage, data.b[0].borrowedUSD, data.b[0].farmingAPR, data.b[1].borrowedUSD, data.b[1].farmingAPR);
  const leveragedAPR = data.totalAPR + farmingAPR - borrowAPR;

  return (
    <InteractionModalContainer title="Leverage" show={show} toggleShow={toggleShow}>
      <>
        <RiskMetrics changeBorrowedA={data.changeAmounts.bAmountA} changeBorrowedB={data.changeAmounts.bAmountB} changeCollateral={data.changeAmounts.cAmount} />
        <InputAmount
          val={newLeverage}
          setVal={setNewLeverage}
          suffix={'x'}
          maxTitle={'Max leverage'}
          max={data.maxLeverage}
          min={data.currentLeverage}
        />
        <div className="transaction-recap">
          <BorrowFee amount={data.changeAmounts.bAmountA} symbol={data.b[0].symbol} />
          <BorrowFee amount={data.changeAmounts.bAmountB} symbol={data.b[1].symbol} />
          <Row className="mt-2">
            <Col xs={6}>You will borrow:</Col>
            <Col xs={6} className="text-right">{formatFloat(data.changeAmounts.bAmountA)} {data.b[0].symbol}</Col>
          </Row>
          <Row>
            <Col xs={6}>You will borrow:</Col>
            <Col xs={6} className="text-right">{formatFloat(data.changeAmounts.bAmountB)} {data.b[1].symbol}</Col>
          </Row>
          <Row>
            <Col xs={6}>You will get:</Col>
            <Col xs={6} className="text-right">{formatFloat(data.changeAmounts.cAmount)} {data.symbol}</Col>
          </Row>
          { data.totalAPR > 0 && (
            <Row className="mt-2">
              <Col xs={6}>Trading fees APR:</Col>
              <Col xs={6} className="text-right">+{formatPercentage(data.totalAPR)}</Col>
            </Row>
          ) }
          { data.stakingRewards.map((reward, key: any) => (reward.APR > 0) && (
            <Row key={key}>
              <Col xs={6}>{ reward.symbol } Reward APR:</Col>
              <Col xs={6} className="text-right">+{formatPercentage(reward.APR * newLeverage)}</Col>
            </Row>
          ))}
          { farmingRewards.map((reward, key: any) => (reward.APR > 0) && (
            <Row key={key}>
              <Col xs={6}>{ reward.symbol } Farming APR:</Col>
              <Col xs={6} className="text-right">+{formatPercentage(reward.APR)}</Col>
            </Row>
          ))}
          <Row>
            <Col xs={6}>Borrow APR:</Col>
            <Col xs={6} className="text-right">-{formatPercentage(borrowAPR)}</Col>
          </Row>
          <Row>
            <Col xs={6}>Estimated APR:</Col>
            <Col xs={6} className="text-right estimated-apr">{formatPercentage(leveragedAPR)}</Col>
          </Row>
        </div>
        <Row className="interaction-row">
          <Col xs={6}>
            <InteractionButton name={"Approve " + data.b[0].symbol} onCall={onApproveA} state={approvalStateA} />
          </Col>
          <Col xs={6}>
            <InteractionButton name={"Approve " + data.b[1].symbol} onCall={onApproveB} state={approvalStateB} />
          </Col>
        </Row>
        <Row className="interaction-row">
          <Col>
            <InteractionButton name="Leverage"  onCall={onLeverage} state={leverageState} />
          </Col>
        </Row>
      </>
    </InteractionModalContainer>
  );
}